import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const WORKFLOW_STYLES = {
  "New Enquiry": "bg-stone-100 text-stone-700",
  "Awaiting Payment": "bg-amber-50 text-amber-800",
  "Payment Received": "bg-emerald-50 text-emerald-800",
  "Booking Pending": "bg-sky-50 text-sky-800",
  "Booking Confirmed": "bg-[#C5A059]/15 text-[#9E7B3A]",
  "Completed": "bg-[#C5A059]/15 text-[#9E7B3A]",
  "Cancelled": "bg-red-50 text-red-700",
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: "Australia/Sydney" });

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/customers", form);
      toast.success("Customer added");
      setCreating(false);
      setForm({ name: "", email: "", phone: "" });
      navigate(`/admin/customers/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-customers-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl font-light mb-2">Customers</h1>
          <p className="text-sm text-[#4A4A4A]">Every conversation, payment and booking — one timeline per client.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="create-customer-button"
          className="flex items-center gap-2 bg-[#1A1A1A] text-white text-xs tracking-[0.2em] uppercase px-5 py-3 hover:bg-[#9E7B3A] transition-colors duration-300">
          <Plus className="w-4 h-4" /> New Customer
        </button>
      </div>

      {customers.length === 0 ? (
        <div className="bg-white border border-[#C5A059]/25 px-8 py-16 text-center" data-testid="customers-empty">
          <Users className="w-8 h-8 text-[#C5A059] mx-auto mb-4" />
          <p className="font-serif text-2xl font-light mb-2">No customers yet</p>
          <p className="text-sm text-[#4A4A4A]">Add your first client to start tracking their journey.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#C5A059]/25 overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="border-b border-[#C5A059]/20 text-left">
                {["Customer", "Contact", "Stage", "Events", "Since"].map((h) => (
                  <th key={h} className="px-5 py-4 text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#C5A059]/10">
              {customers.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/admin/customers/${c.id}`)} data-testid={`customer-row-${c.id}`}
                  className="cursor-pointer hover:bg-[#C5A059]/5 transition-colors duration-200">
                  <td className="px-5 py-4 font-medium">{c.name}</td>
                  <td className="px-5 py-4 text-[#4A4A4A]">
                    <p>{c.email || "—"}</p>
                    <p className="text-xs">{c.phone}</p>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-[10px] tracking-widest uppercase px-2.5 py-1 ${WORKFLOW_STYLES[c.workflow_status] || ""}`}>
                      {c.workflow_status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-[#4A4A4A]">{c.event_count ?? 0}</td>
                  <td className="px-5 py-4 text-[#4A4A4A]">{fmtDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md rounded-none" data-testid="create-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">New Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4 mt-2 text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Name</Label>
              <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="customer-name-input" className="rounded-none" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="customer-email-input" className="rounded-none" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                data-testid="customer-phone-input" className="rounded-none" />
            </div>
            <button type="submit" disabled={saving} data-testid="customer-create-submit"
              className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
              {saving ? "Adding…" : "Add Customer"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
